/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from "react";

import { MigrateUserRequestProto_Body } from "@noom/noom-contracts/noom_contracts/backend/retriage/retriage_service";
import {
  MskPainAreaProto,
  MskPainAreaProto_Value,
} from "@noom/noom-contracts/noom_contracts/healthcare/shared";
import { Api } from "@noom/noomscape";

import { ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import { SurveyAnswers } from "@/contexts/app-context";
import { Partner, RetriageEligibility } from "@/models";
import { getHeightInCm, getWeightInKg } from "@/utils/formatAsUserData";
import { getOfferingFromProgram } from "@/utils/programs";
import { buildAddressProto } from "@/utils/proto";
import { sanitizeRetriageAnswers } from "@/utils/retriage";

function buildRequest(
  surveyAnswers: SurveyAnswers,
  selectedProgram: ProgramOffering | null,
  qualifiedPrograms: ProgramOffering[],
  retriageEligibility: RetriageEligibility,
  partnerInfo: Partner,
  b2bIdentifiedUserId: string,
): MigrateUserRequestProto_Body {
  if (!selectedProgram) {
    throw new Error("Cannot migrate user: no selected program");
  }

  const request: MigrateUserRequestProto_Body = {
    accessCode: retriageEligibility.accessCode!,
    ...(!!retriageEligibility.eligibilityId && {
      eligibilityId: retriageEligibility.eligibilityId,
    }),
    survey: {},
    surveyJson: sanitizeRetriageAnswers(surveyAnswers),
    offering: getOfferingFromProgram(selectedProgram, qualifiedPrograms),
    partnerId: partnerInfo.id,
    completionStatus: "COMPLETE",
    wasOfferedDpp: !!surveyAnswers.wasOfferedDpp,
    wasOfferedMed: !!surveyAnswers.wasOfferedMed,
    wasOfferedDiabetesSupport: !!surveyAnswers.wasOfferedDiabetesSupport,
    heightCm: getHeightInCm(
      surveyAnswers.heightWeight!.heightFeetPortion!,
      surveyAnswers.heightWeight!.heightInchesPortion!,
    ),
    weightKg: getWeightInKg(surveyAnswers.heightWeight!.weight!),
    dppSurveyData: {
      phq1Answer: surveyAnswers.phq1,
      phq2Answer: surveyAnswers.phq2,
      phq3Answer: surveyAnswers.phq3,
      phq4Answer: surveyAnswers.phq4,
      mskPain: mskPainAnswersToProto(surveyAnswers.mskPain),
      mskPainLevel: surveyAnswers.mskPainLevel,
      initialA1c: surveyAnswers.a1c,
    },
    diabetesSurveyData: {
      diabetesType: surveyAnswers.diabetesType,
      prediabetesHistory: surveyAnswers.prediabetesHistory ?? [],
    },
  };
  if (selectedProgram === "MED") {
    request.medRetriageData = {
      userId: b2bIdentifiedUserId,
      homeAddress: buildAddressProto(
        surveyAnswers.medSignup!.address,
        surveyAnswers.medSignup!.phoneNumber,
      ),
      mainReason: surveyAnswers.mainReason!,
      familyHistory: surveyAnswers.familyHistory!,
      pregnant: surveyAnswers.pregnant! || "no",
      healthConditions: surveyAnswers.healthConditions || [],
      heightFeet: surveyAnswers.heightWeight!.heightFeetPortion!,
      heightInch: surveyAnswers.heightWeight!.heightInchesPortion!,
      weightLbs: surveyAnswers.heightWeight!.weight!,
      useMedications: Number(surveyAnswers.medications) > 0 ? "yes" : "no",
      suicideOrSelfHarm1: surveyAnswers.phq4!,
      suicideOrSelfHarm2: surveyAnswers.phq3!,
      glp1Medication: surveyAnswers.glp1Medication ?? "no",
    };
  }
  if (surveyAnswers.scaleOptIn) {
    request.scaleShippingAddress = buildAddressProto(
      surveyAnswers.medSignup!.shippingAddress ||
        surveyAnswers.medSignup!.address,
    );
  }
  if (
    surveyAnswers.liabilityWaiverEatingDisorder ||
    surveyAnswers.liabilityWaiverBMI ||
    surveyAnswers.liabilityWaiverPregnant
  ) {
    request.acceptedLiabilityWaiver = true;
  }
  return request;
}

export const useMigrateUser = () => {
  const [apiError, setError] = useState<unknown>();
  const [apiLoading, setApiLoading] = useState(true);
  const {
    surveyAnswers,
    retriageEligibility,
    partnerInfo,
    b2bIdentifiedUserId,
    qualifiedPrograms,
    selectedProgram,
  } = useAppContext();

  const migrateUser = async () => {
    if (!retriageEligibility) {
      throw Error("Cannot migrate to med: No RetriageEligibility found");
    }
    if (!partnerInfo) {
      throw Error("Cannot migrate to med: No partner info");
    }

    try {
      await Api.call(
        "retriage.migrateUser",
        Api.api.retriage.migrateUser,
        buildRequest(
          surveyAnswers,
          selectedProgram,
          qualifiedPrograms,
          retriageEligibility,
          partnerInfo,
          b2bIdentifiedUserId,
        ),
      );
      setApiLoading(false);
    } catch (e) {
      const err = e as Error;
      setError(err.message);
      setApiLoading(false);
    }
  };

  return { migrateUser, apiLoading, apiError };
};

function mskPainAnswersToProto(
  mskPain: string[] | undefined,
): MskPainAreaProto[] {
  return (mskPain || []).map((pain) => {
    let value: MskPainAreaProto_Value;
    switch (pain) {
      case "knee":
        value = "KNEE";
        break;
      case "hip":
        value = "HIP";
        break;
      case "lowerBack":
        value = "LOWER_BACK";
        break;
      case "upperBack":
        value = "UPPER_BACK";
        break;
      case "none":
        value = "NONE";
        break;
      default:
        value = "UNKNOWN";
    }
    return { value };
  });
}
